import Style from './panelStyles.module.scss';
import { useNode } from '@craftjs/core';
import { FormTitleProps } from '../../types/BaseControlTypes';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { SettingsInput } from './uiKit/input/settingsInput';
import { hasSection } from './helpers/helper';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useDataKeyEditing } from './useDataKeyEditing';
import { AppCheckboxInput } from 'uikit/inputs';

type TitleSection = 'Title' | 'SectionTitle' | 'Prefix' | 'Tooltip' | 'Description' | 'DataKey';

export type TitleProps = {
  sections: TitleSection[];
};

export const TitlePanel = ({ sections }: TitleProps) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Title');
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as FormTitleProps,
  }));

  const { hasError, component: dataKeySection } = useDataKeyEditing();

  return (
    <PanelContainer header={t('Header')} expanded={hasError} panelName={'Title'}>
      {hasSection(sections, 'SectionTitle') && (
        <ControlsGroup
          header={t('SectionTitle')}
          collapsible={true}
          contentClassName={Style.flexContainer}
          expanded={!!props.sectionTitle}
          onOpen={() => {
            setProp((prop: FormTitleProps) => {
              prop.sectionTitle = t('SectionTitle');
              prop.isCollapsible = true;
            }, 500);
          }}
          onClose={() => {
            setProp((prop: FormTitleProps) => {
              prop.sectionTitle = undefined;
              prop.isCollapsible = undefined;
            }, 500);
          }}
        >
          <SettingsInput
            hideErrorText
            placeholder={t('SectionTitlePlaceholder')}
            value={props.sectionTitle}
            onChange={(e) =>
              setProp((p: FormTitleProps) => {
                p.sectionTitle = e.target.value || undefined;
              }, 500)
            }
          />
          <AppCheckboxInput
            label={t('SectionTitleMakeCollapsible')}
            checked={props.isCollapsible}
            onChange={(e) =>
              setProp((p: FormTitleProps) => {
                p.isCollapsible = e.target.checked || undefined;
              }, 500)
            }
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'Title') && (
        <ControlsGroup
          header={t('Title')}
          collapsible={true}
          contentClassName={Style.flexContainer}
          expanded={!!props.title}
          onOpen={() => {
            setProp((prop: FormTitleProps) => {
              prop.title = t('Title');
            }, 500);
          }}
          onClose={() => {
            setProp((prop: FormTitleProps) => {
              prop.title = undefined;
            }, 500);
          }}
        >
          <SettingsInput
            placeholder={t('TitlePlaceholder')}
            multiple={true}
            value={props.title}
            onChange={(e) =>
              setProp((p: FormTitleProps) => {
                p.title = e.target.value || undefined;
              }, 500)
            }
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'DataKey') && dataKeySection}

      {hasSection(sections, 'Prefix') && (
        <ControlsGroup
          header={t('Prefix')}
          collapsible={true}
          contentClassName={Style.flexContainer}
          expanded={!!props.prefix}
          onOpen={() => {
            setProp((prop: FormTitleProps) => {
              prop.prefix = t('Prefix');
            }, 500);
          }}
          onClose={() => {
            setProp((prop: FormTitleProps) => {
              prop.prefix = undefined;
            }, 500);
          }}
        >
          <SettingsInput
            placeholder={t('PrefixPlaceholder')}
            value={props.prefix}
            onChange={(e) =>
              setProp((p: FormTitleProps) => {
                p.prefix = e.target.value || undefined;
              }, 500)
            }
          />
        </ControlsGroup>
      )}
      {hasSection(sections, 'Tooltip') && (
        <ControlsGroup
          header={t('Tooltip')}
          collapsible={true}
          contentClassName={Style.flexContainer}
          expanded={!!props.tooltip}
          onOpen={() => {
            setProp((prop: FormTitleProps) => {
              prop.tooltip = t('Tooltip');
            }, 500);
          }}
          onClose={() => {
            setProp((prop: FormTitleProps) => {
              prop.tooltip = undefined;
            }, 500);
          }}
        >
          <SettingsInput
            placeholder={t('TooltipPlaceholder')}
            value={props.tooltip}
            onChange={(e) =>
              setProp((p: FormTitleProps) => {
                p.tooltip = e.target.value || undefined;
              }, 500)
            }
          />
        </ControlsGroup>
      )}
      {hasSection(sections, 'Description') && (
        <ControlsGroup
          header={t('Description')}
          collapsible={true}
          contentClassName={Style.flexContainer}
          expanded={!!props.description}
          onOpen={() => {
            setProp((prop: FormTitleProps) => {
              prop.description = t('Description');
            }, 500);
          }}
          onClose={() => {
            setProp((prop: FormTitleProps) => {
              prop.description = undefined;
            }, 500);
          }}
        >
          <SettingsInput
            placeholder={t('DescriptionPlaceholder')}
            value={props.description}
            multiple={true}
            onChange={(e) =>
              setProp((p: FormTitleProps) => {
                p.description = e.target.value || undefined;
              }, 500)
            }
          />
        </ControlsGroup>
      )}
    </PanelContainer>
  );
};
